import { Link, useNavigate } from 'react-router';
import type { Property } from 'csstype';
import type { LayoutValue } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import type { TileProps } from '../../type';
import ArchizeenTile from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { ClientOnly } from 'remix-utils';
import { getFormattedLocalisedDate } from '@modules/date-time/util/get-localised-date';
import { useLocalisation } from '@archipro-website/localisation';
import type { GetTileHotButtonsProps } from '~/modules/root';
import { useTracker } from '../../../../../../tracker/src/react-bindings';

interface ArticleVideoTileProps {
    article: TileProps;
    imageWidth: number;
    showCategory?: boolean;
    disableVideo?: boolean;
    iconSizeOverride?: number;
    getTileTopActions?: <T>({
        item,
        adminItem,
        onDeleteClick,
        editOnSameTab,
    }: GetTileHotButtonsProps<T>) => React.ReactNode;
}

const ArticleVideoTile = ({
    article,
    imageWidth,
    showCategory,
    disableVideo,
    iconSizeOverride,
    getTileTopActions,
}: ArticleVideoTileProps) => {
    const { localiseDate } = useLocalisation();
    const { mobile } = useAppDisplayModeContext();
    const {
        topBanner,
        ID,
        label,
        categoryLabel,
        homePinned: _homePinned,
        ...tileProps
    } = article;
    const navigate = useNavigate();
    const tracker = useTracker();

    const hasVideo =
        !disableVideo && topBanner?.isVideoBanner && topBanner.videoUrl;

    const video = hasVideo
        ? {
              src: topBanner.videoUrl || '',
              coverImage: {
                  src: topBanner.videoCover || '',
                  width: imageWidth,
                  height: imageWidth * 0.5625,
                  layout: 'intrinsic' as LayoutValue,
                  objectFit: 'cover' as Property.ObjectFit,
                  objectPosition: 'center',
                  alt: article.title,
              },
              muted: true,
              controls: false,
              playsInline: true,
              playOnMouseOver: !mobile,
              variables: {
                  playIconSizeDesktop: iconSizeOverride ?? 130,
                  playIconSizeMobile: iconSizeOverride ?? 71,
                  coverImagePosition: 'absolute',
              },
              onPause: () => {
                  navigate(`${article.link}?video=1`);
              },
              disabled: mobile,
              trackPlayEvent: () => {
                  tracker.log('Video', {
                      url: new URL(window.location.href),
                  });
              },
              //tech debt: look into better way to handle link with mouseover autoplay
          }
        : undefined;

    const formattedDate = getFormattedLocalisedDate(localiseDate, article.date);

    const archizeenTile = (
        <ArchizeenTile
            key={`archizeen_tile--${ID}`}
            as={Link}
            to={hasVideo ? `${article.link}?video=1` : article.link}
            prefetch="intent"
            {...tileProps}
            label={showCategory ? categoryLabel : label}
            date={formattedDate}
            variables={{
                ...article.variables,
            }}
            variant={article.variant}
            image={{
                width: imageWidth,
                height: imageWidth * 0.5625,
                src: article.image,
                alt: article.title,
                sizes: getImagesSizes(imageWidth),
            }}
            video={video}
            title={article.title}
            impression={{
                professionalID: article.professionalID,
                itemID: ID,
                type: 'LibraryItem_Directory',
            }}
            topActions={getTileTopActions?.({ item: article })}
            articleId={ID}
        />
    );

    return video ? (
        <ClientOnly fallback={<></>}>{() => archizeenTile}</ClientOnly>
    ) : (
        archizeenTile
    );
};

export default ArticleVideoTile;
